import React from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
    Card,
    CardBody,
    CardHeader,
    Typography,
    Chip,
    Button,
    Spinner,
} from '@material-tailwind/react';
import {
    Calendar,
    Clock,
    DollarSign,
    FileText,
    MapPin,
    CheckCircle,
    XCircle,
    Clock3,
    ArrowLeft,
    User,
    Building2,
    Armchair,
    CalendarRange,
    Repeat,
    MessageSquare,
} from 'lucide-react';
import moment from 'moment';
import { useGetBookingById } from '../../custom-hooks/use-bookings';
import { URLS } from '../../router-x/routes';

const BookingDetailPage = () => {
    const { bookingId } = useParams();
    const navigate = useNavigate();

    // Fetch booking details
    const { data: booking, isLoading, error } = useGetBookingById(bookingId);

    // Status badge color mapping
    const getStatusColor = (status) => {
        const colors = {
            APPROVED: 'green',
            PENDING: 'amber',
            REJECTED: 'red',
            CANCELLED: 'gray',
        };
        return colors[status] || 'blue';
    };

    // Rent frequency display mapping
    const getRentFrequencyLabel = (frequency) => {
        const labels = {
            once: 'One Time',
            daily: 'Daily',
            weekly: 'Weekly',
            monthly: 'Monthly',
        };
        return labels[frequency] || frequency;
    };

    if (isLoading) {
        return (
            <div className="flex justify-center items-center min-h-screen">
                <Spinner className="h-12 w-12" />
            </div>
        );
    }

    if (error || !booking) {
        return (
            <div className="container mx-auto px-4 py-8">
                <Card className="bg-red-50">
                    <CardBody className="text-center">
                        <XCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
                        <Typography variant="h5" color="red">
                            Failed to load booking details
                        </Typography>
                        <Typography color="gray" className="mt-2">
                            {error?.message || 'Booking not found'}
                        </Typography>
                        <Button
                            variant="text"
                            onClick={() => navigate(-1)}
                            className="mt-4"
                        >
                            Back to Bookings
                        </Button>
                    </CardBody>
                </Card>
            </div>
        );
    }

    return (
        <div className="bg-gray-50">
            <div className="container mx-auto px-4 py-8">
                {/* Header */}
                <div className="mb-6">
                    <Button
                        variant="text"
                        className="flex items-center gap-2 mb-4"
                        onClick={() => navigate(-1)}
                    >
                        <ArrowLeft className="h-4 w-4" />
                        Back
                    </Button>

                    <div className="flex items-center justify-between flex-wrap gap-4">
                        <div>
                            <Typography variant="h3" className="mb-2">
                                Booking Details
                            </Typography>
                            <Typography color="gray" className="flex items-center gap-2 text-xs">
                                <FileText className="h-3 w-3" />
                                ID: {booking._id}
                            </Typography>
                        </div>
                        <div className="flex items-center gap-3">
                            <Chip
                                value={booking.status}
                                color={getStatusColor(booking.status)}
                                className="rounded-full"
                                icon={
                                    booking.status === 'APPROVED' ? (
                                        <CheckCircle className="h-4 w-4" />
                                    ) : (
                                        <Clock3 className="h-4 w-4" />
                                    )
                                }
                            />
                            <Chip
                                value={booking.isActive ? 'Active' : 'Inactive'}
                                color={booking.isActive ? 'green' : 'gray'}
                                className="rounded-full"
                            />
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Booking Information */}
                    <Card className="shadow-none border border-gray-200">
                        <CardHeader
                            floated={false}
                            shadow={false}
                            className="bg-blue-50 p-4 rounded-t-lg"
                        >
                            <div className="flex items-center gap-2">
                                <Calendar className="h-5 w-5 text-blue-600" />
                                <Typography variant="h6" color="blue-gray">
                                    Booking Information
                                </Typography>
                            </div>
                        </CardHeader>
                        <CardBody className="space-y-4">
                            <div className="flex items-start gap-3">
                                <CalendarRange className="h-5 w-5 text-gray-600 mt-1" />
                                <div className="flex-1">
                                    <Typography variant="small" className="text-gray-600">
                                        Start Date
                                    </Typography>
                                    <Typography variant="h6" color="blue-gray">
                                        {moment(booking.startDate).format('MMM DD, YYYY')}
                                    </Typography>
                                </div>
                            </div>

                            {booking.endDate && (
                                <div className="flex items-start gap-3">
                                    <CalendarRange className="h-5 w-5 text-gray-600 mt-1" />
                                    <div className="flex-1">
                                        <Typography variant="small" className="text-gray-600">
                                            End Date
                                        </Typography>
                                        <Typography variant="h6" color="blue-gray">
                                            {moment(booking.endDate).format('MMM DD, YYYY')}
                                        </Typography>
                                    </div>
                                </div>
                            )}

                            {booking.dailyStartTime && (
                                <div className="flex items-start gap-3">
                                    <Clock className="h-5 w-5 text-gray-600 mt-1" />
                                    <div className="flex-1">
                                        <Typography variant="small" className="text-gray-600">
                                            Daily Start Time
                                        </Typography>
                                        <Typography variant="h6" color="blue-gray">
                                            {moment(booking.dailyStartTime).format('hh:mm A')}
                                        </Typography>
                                    </div>
                                </div>
                            )}

                            {booking.dailyEndTime && (
                                <div className="flex items-start gap-3">
                                    <Clock className="h-5 w-5 text-gray-600 mt-1" />
                                    <div className="flex-1">
                                        <Typography variant="small" className="text-gray-600">
                                            Daily End Time
                                        </Typography>
                                        <Typography variant="h6" color="blue-gray">
                                            {moment(booking.dailyEndTime).format('hh:mm A')}
                                        </Typography>
                                    </div>
                                </div>
                            )}

                            <div className="border-t pt-4 mt-4 flex items-center gap-2">
                                <div className="flex items-center gap-2 text-gray-600">
                                    <Clock3 className="h-4 w-4" />
                                    <Typography variant="small">Created</Typography>
                                </div>
                                <Typography variant="small" color="blue-gray">
                                    {moment(booking.createdDate).format('lll')}
                                </Typography>
                            </div>

                            {booking.lastUpdated && (
                                <div className="flex items-center gap-2">
                                    <div className="flex items-center gap-2 text-gray-600">
                                        <Clock3 className="h-4 w-4" />
                                        <Typography variant="small">Last Updated</Typography>
                                    </div>
                                    <Typography variant="small" color="blue-gray">
                                        {moment(booking.lastUpdated).format('lll')}
                                    </Typography>
                                </div>
                            )}
                        </CardBody>
                    </Card>

                    {/* Space Details */}
                    <Card className="shadow-none border border-gray-200">
                        <CardHeader
                            floated={false}
                            shadow={false}
                            className="bg-purple-50 p-4 rounded-t-lg"
                        >
                            <div className="flex items-center gap-2">
                                <MapPin className="h-5 w-5 text-purple-600" />
                                <Typography variant="h6" color="blue-gray">
                                    Space Details
                                </Typography>
                            </div>
                        </CardHeader>
                        <CardBody className="space-y-4">
                            {booking.seat && (
                                <div className="flex items-start gap-3">
                                    <Armchair className="h-5 w-5 text-gray-600 mt-1" />
                                    <div className="flex-1">
                                        <Typography variant="small" className="text-gray-600">
                                            Seat
                                        </Typography>
                                        <Typography variant="h6" color="blue-gray">
                                            {booking.seat?.name || booking.seat?.number || 'N/A'}
                                        </Typography>
                                        {booking.seat?.location && (
                                            <Typography variant="small" color="gray">
                                                {booking.seat.location}
                                            </Typography>
                                        )}
                                    </div>
                                </div>
                            )}

                            {booking.office && (
                                <div className="flex items-start gap-3">
                                    <Building2 className="h-5 w-5 text-gray-600 mt-1" />
                                    <div className="flex-1">
                                        <Typography variant="small" className="text-gray-600">
                                            Office
                                        </Typography>
                                        <Link to={`${URLS.ADMIN.OFFICE}/${booking.office._id}`} target="_blank">
                                            <Typography variant="h6" color="blue-gray" className="cursor-pointer text-blue-600 hover:text-blue-800 duration-300">
                                                {booking.office?.code || booking.office?.number || 'N/A'} <span className="text-xs text-gray-500">({booking.office?.capacity} seats)</span>
                                            </Typography>
                                        </Link>
                                        {booking.office?.location && (
                                            <Typography variant="small" color="gray">
                                                {booking.office.location}
                                            </Typography>
                                        )}
                                    </div>
                                </div>
                            )}

                            {!booking.seat && !booking.office && (
                                <div className="text-center py-4">
                                    <Typography color="gray">No space assigned</Typography>
                                </div>
                            )}
                        </CardBody>
                    </Card>

                    {/* Rent Information */}
                    <Card className="shadow-none border border-gray-200">
                        <CardHeader
                            floated={false}
                            shadow={false}
                            className="bg-green-50 p-4 rounded-t-lg"
                        >
                            <div className="flex items-center gap-2">
                                <DollarSign className="h-5 w-5 text-green-600" />
                                <Typography variant="h6" color="blue-gray">
                                    Rent Information
                                </Typography>
                            </div>
                        </CardHeader>
                        <CardBody className="space-y-4">
                            <div className="flex items-start gap-3">
                                <Repeat className="h-5 w-5 text-gray-600 mt-1" />
                                <div className="flex-1">
                                    <Typography variant="small" className="text-gray-600">
                                        Rent Frequency
                                    </Typography>
                                    <Typography variant="h6" color="blue-gray">
                                        {getRentFrequencyLabel(booking.rentFrequency)}
                                    </Typography>
                                </div>
                            </div>

                            <div className="flex items-start gap-3">
                                <DollarSign className="h-5 w-5 text-gray-600 mt-1" />
                                <div className="flex-1">
                                    <Typography variant="small" className="text-gray-600">
                                        Rent Rate
                                    </Typography>
                                    <Typography variant="h6" color="blue-gray">
                                        {booking.rentCurrency} {booking.rentRate?.toLocaleString() || 0}
                                    </Typography>
                                </div>
                            </div>

                            {booking.rentTotal && (
                                <div className="flex items-start gap-3 bg-green-50 p-3 rounded-lg">
                                    <DollarSign className="h-5 w-5 text-green-600 mt-1" />
                                    <div className="flex-1">
                                        <Typography variant="small" className="text-gray-600">
                                            Total Rent
                                        </Typography>
                                        <Typography variant="h5" color="green">
                                            {booking.rentCurrency} {booking.rentTotal?.toLocaleString() || 0}
                                        </Typography>
                                    </div>
                                </div>
                            )}
                        </CardBody>
                    </Card>

                    {/* Contract Information */}
                    <Card className="shadow-none border border-gray-200">
                        <CardHeader
                            floated={false}
                            shadow={false}
                            className="bg-indigo-50 p-4 rounded-t-lg"
                        >
                            <div className="flex items-center gap-2">
                                <FileText className="h-5 w-5 text-indigo-600" />
                                <Typography variant="h6" color="blue-gray">
                                    Contract Information
                                </Typography>
                            </div>
                        </CardHeader>
                        <CardBody className="space-y-4">
                            {booking.contract ? (
                                <>
                                    <div className="flex items-start gap-3">
                                        <User className="h-5 w-5 text-gray-600 mt-1" />
                                        <div className="flex-1">
                                            <Typography variant="small" className="text-gray-600">
                                                Contract ID
                                            </Typography>
                                            <Typography variant="h6" color="blue-gray" className="cursor-pointer text-blue-600 hover:text-blue-800 duration-300 text-xs">
                                                {booking.contract._id || booking.contract}
                                            </Typography>
                                        </div>
                                    </div>

                                    {booking.contract.contractNumber && (
                                        <div className="flex items-start gap-3">
                                            <FileText className="h-5 w-5 text-gray-600 mt-1" />
                                            <div className="flex-1">
                                                <Typography variant="small" className="text-gray-600">
                                                    Contract Number
                                                </Typography>
                                                <Typography variant="h6" color="blue-gray">
                                                    {booking.contract.contractNumber}
                                                </Typography>
                                            </div>
                                        </div>
                                    )}

                                    <Button
                                        variant="outlined"
                                        size="sm"
                                        className="w-full"
                                        onClick={() => navigate(`/contracts/${booking.contract._id || booking.contract}`)}
                                    >
                                        View Contract Details
                                    </Button>
                                </>
                            ) : (
                                <div className="text-center py-4">
                                    <Typography color="gray">No contract associated</Typography>
                                </div>
                            )}
                        </CardBody>
                    </Card>
                </div>

                {/* Comments Section - Full Width */}
                {booking.comment && (
                    <Card className="mt-6 shadow-none border border-gray-200">
                        <CardHeader
                            floated={false}
                            shadow={false}
                            className="bg-amber-50 p-4 rounded-t-lg"
                        >
                            <div className="flex items-center gap-2">
                                <MessageSquare className="h-5 w-5 text-amber-600" />
                                <Typography variant="h6" color="blue-gray">
                                    Comments
                                </Typography>
                            </div>
                        </CardHeader>
                        <CardBody>
                            <Typography color="blue-gray" className="whitespace-pre-wrap">
                                {booking.comment}
                            </Typography>
                        </CardBody>
                    </Card>
                )}

                {/* Action Buttons */}
                <div className="mt-6 flex gap-4 justify-end">
                    <Button
                        variant="outlined"
                        onClick={() => navigate(`/bookings/${bookingId}/edit`)}
                    >
                        Edit Booking
                    </Button>
                    <Button color="blue" onClick={() => navigate(-1)}>
                        Back to Bookings
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default BookingDetailPage;